import type { ConfigData, PlayerData } from '../../../types';
import { StateManager } from '../../../managers';
import { formatStatName } from '../../components/GameTerms';

/** Template IDs that are not yet implemented — exclude from codex.
 *  Must be kept in sync with NYI_TEMPLATE_IDS in src/config/units.ts (backend canonical list). */
export const NYI_TEMPLATE_IDS = new Set(['reefsinger', 'stalker']);

export type CodexChapter = 'strands' | 'skills' | 'items' | 'uniques' | 'masteries' | 'mechanics' | 'effects' | 'bestiary' | 'factions' | 'affixes' | 'elements' | 'achievements';

export const CHAPTERS: { id: CodexChapter; icon: string; label: string }[] = [
  { id: 'strands', icon: '🧬', label: 'Strands' },
  { id: 'skills', icon: '💠', label: 'Skills' },
  { id: 'items', icon: '⚔️', label: 'Items' },
  { id: 'uniques', icon: '✨', label: 'Uniques' },
  { id: 'masteries', icon: '📖', label: 'Masteries' },
  { id: 'mechanics', icon: '⚙️', label: 'Mechanics' },
  { id: 'effects', icon: '🔶', label: 'Effects' },
  { id: 'bestiary', icon: '🐉', label: 'Bestiary' },
  { id: 'factions', icon: '🏛', label: 'Factions' },
  { id: 'elements', icon: '⚡', label: 'Elements' },
  { id: 'achievements', icon: '🏆', label: 'Achievements' },
];

// Elements displayed in the weakness chart
export const CHART_ELEMENTS = [
  'steel', 'brute', 'finesse',
  'fire', 'water', 'lightning',
  'nature', 'wind', 'earth',
  'holy', 'dark', 'astral',
];

// ── Shared helpers ─────────────────────────────────────────

export function getConfig(): ConfigData | null {
  return StateManager.get('configData') as ConfigData | null;
}

export function getDiscoveredStrands(): Set<string> {
  const player = StateManager.get('playerData') as PlayerData | null;
  try { return new Set(JSON.parse(player?.discoveredStrands || '[]')); } catch { return new Set(); }
}

export function getDiscoveredEnemies(): Set<string> {
  const player = StateManager.get('playerData') as PlayerData | null;
  try { return new Set(JSON.parse(player?.discoveredEnemies || '[]')); } catch { return new Set(); }
}

export function getDiscoveredUniques(): Set<string> {
  const player = StateManager.get('playerData') as PlayerData | null;
  try { return new Set(JSON.parse(player?.discoveredUniques || '[]')); } catch { return new Set(); }
}

export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function formatStat(stat: string): string {
  return formatStatName(stat);
}

const CATEGORY_LABELS: Record<string, string> = {
  combat_stat: 'Stat', flat_damage: 'Damage', resistance: 'Resistance',
  penetration: 'Penetration', mastery: 'Mastery', mastery_cost_reduction: 'Mastery Cost',
  tag_increased: 'Increased',
};

export function formatCategoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Callback type for switching chapters from within a chapter renderer */
export type SwitchChapterFn = (chapter: CodexChapter) => void;
